import { useEffect, useState } from "react";
import PasswordStrengthChecklist from "../auth/PasswordStrengthChecklist";

export default function ExportMenu({
  open,
  activeAction,
  userEmail,
  userFirstName,
  userLastName,
  accountNeedsPasswordSetup = false,
  onClose,
  onActionToggle,
  onSpreadsheetExport,
  onAccountUpdate,
  onAccountPasswordSetup = async () => {},
  onSignOut,
}) {
  const [shouldRender, setShouldRender] = useState(open);
  const [accountOpen, setAccountOpen] = useState(false);
  const [firstName, setFirstName] = useState(userFirstName || "");
  const [lastName, setLastName] = useState(userLastName || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [accountStatus, setAccountStatus] = useState(null);
  const [savingAccount, setSavingAccount] = useState(false);
  const [sendingPasswordSetup, setSendingPasswordSetup] = useState(false);

  useEffect(() => {
    if (open) {
      setShouldRender(true);
      return undefined;
    }

    const timeout = window.setTimeout(() => setShouldRender(false), 230);
    return () => window.clearTimeout(timeout);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setAccountOpen(false);
      setCurrentPassword("");
      setPassword("");
      setPasswordConfirm("");
      setChangingPassword(false);
      setAccountStatus(null);
      setSendingPasswordSetup(false);
      return;
    }

    setFirstName(userFirstName || "");
    setLastName(userLastName || "");
  }, [open, userFirstName, userLastName]);

  if (!shouldRender) return null;

  const closing = shouldRender && !open;

  const handleAccountSubmit = async (event) => {
    event.preventDefault();
    setAccountStatus(null);
    setSavingAccount(true);

    try {
      await onAccountUpdate({
        firstName,
        lastName,
        currentPassword,
        password,
        passwordConfirm,
      });
      setCurrentPassword("");
      setPassword("");
      setPasswordConfirm("");
      setChangingPassword(false);
      setAccountOpen(false);
    } catch (error) {
      setAccountStatus({
        type: "error",
        message: error.message || "Nao foi possivel atualizar a conta.",
      });
    } finally {
      setSavingAccount(false);
    }
  };

  const handlePasswordSetup = async () => {
    setAccountStatus(null);
    setSendingPasswordSetup(true);

    try {
      await onAccountPasswordSetup();
      setAccountStatus({
        type: "success",
        message: "Enviamos a verificacao para seu email.",
      });
    } catch (error) {
      setAccountStatus({
        type: "error",
        message: error.message || "Nao foi possivel enviar a verificacao.",
      });
    } finally {
      setSendingPasswordSetup(false);
    }
  };

  return (
    <div
      className={`side-menu-backdrop ${closing ? "side-menu-backdrop--closing" : ""}`}
      onClick={onClose}
    >
      <aside
        className={`side-menu ${closing ? "side-menu--closing" : ""}`}
        aria-label="Menu"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="side-menu__header">
          <div>
            <h2>Menu</h2>
          </div>
          <button
            className="side-menu__close"
            type="button"
            onClick={onClose}
            aria-label="Fechar menu"
          >
            ×
          </button>
        </div>

        <div className="side-menu__section">
          <div className="side-menu__section-title">Dados</div>
          <div className="side-menu__action">
            <button
              className="side-menu__item"
              type="button"
              aria-expanded={activeAction === "export"}
              onClick={() => onActionToggle("export")}
            >
              Exportar
            </button>

            {activeAction === "export" && (
              <div className="side-menu__panel motion-expand-panel">
                <button
                  className="side-menu__spreadsheet"
                  type="button"
                  onClick={onSpreadsheetExport}
                >
                  Exportar como planilha
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="side-menu__footer">
          <div className="side-menu__account-row">
            <div className="side-menu__account">
              <div className="side-menu__account-title">Conta</div>
              <div className="side-menu__email">{userEmail}</div>
            </div>
            <button
              className="side-menu__settings"
              type="button"
              onClick={() => setAccountOpen(true)}
              aria-label="Editar dados da conta"
              title="Editar dados da conta"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12 15.5A3.5 3.5 0 1 0 12 8a3.5 3.5 0 0 0 0 7.5Z" />
                <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6v.3a2 2 0 1 1-4 0V21a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1A2 2 0 1 1 4.2 17l.1-.1A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-1.6-1H2.7a2 2 0 1 1 0-4H3a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7A2 2 0 1 1 7 4.2l.1.1a1.7 1.7 0 0 0 1.9.3A1.7 1.7 0 0 0 10 3V2.7a2 2 0 1 1 4 0V3a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1A2 2 0 1 1 19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.3a2 2 0 1 1 0 4H21a1.7 1.7 0 0 0-1.6 1Z" />
              </svg>
            </button>
          </div>
          <button className="side-menu__signout" type="button" onClick={onSignOut}>
            Sair
          </button>
        </div>
      </aside>

      {accountOpen && (
        <div className="account-modal-backdrop" onClick={() => setAccountOpen(false)}>
          <form
            className="account-modal panel"
            onSubmit={handleAccountSubmit}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="account-modal__header">
              <h2>Editar conta</h2>
              <button
                className="side-menu__close"
                type="button"
                onClick={() => setAccountOpen(false)}
                aria-label="Fechar edicao de conta"
              >
                x
              </button>
            </div>

            <div className="account-modal__row">
              <div>
                <label className="field-label" htmlFor="account-first-name">
                  Nome
                </label>
                <input
                  id="account-first-name"
                  className="input-field"
                  type="text"
                  value={firstName}
                  autoComplete="given-name"
                  onChange={(event) => setFirstName(event.target.value)}
                  required
                />
              </div>
              <div>
                <label className="field-label" htmlFor="account-last-name">
                  Sobrenome
                </label>
                <input
                  id="account-last-name"
                  className="input-field"
                  type="text"
                  value={lastName}
                  autoComplete="family-name"
                  onChange={(event) => setLastName(event.target.value)}
                  required
                />
              </div>
            </div>

            {!changingPassword ? (
              <button
                className="account-modal__password-toggle"
                type="button"
                onClick={
                  accountNeedsPasswordSetup
                    ? handlePasswordSetup
                    : () => setChangingPassword(true)
                }
                disabled={sendingPasswordSetup}
              >
                {sendingPasswordSetup
                  ? "Enviando..."
                  : accountNeedsPasswordSetup
                    ? "Cadastrar senha"
                    : "Alterar senha"}
              </button>
            ) : (
              <div className="account-modal__password-fields motion-expand-panel">
                <div className="account-modal__password-current">
                  <label className="field-label" htmlFor="account-current-password">
                    Senha atual
                  </label>
                  <input
                    id="account-current-password"
                    className="input-field"
                    type="password"
                    value={currentPassword}
                    autoComplete="current-password"
                    onChange={(event) => setCurrentPassword(event.target.value)}
                    required
                  />
                </div>
                <div className="account-modal__row">
                  <div>
                    <label className="field-label" htmlFor="account-password">
                      Nova senha
                    </label>
                    <input
                      id="account-password"
                      className="input-field"
                      type="password"
                      value={password}
                      autoComplete="new-password"
                      minLength={8}
                      onChange={(event) => setPassword(event.target.value)}
                    />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="account-password-confirm">
                      Confirmar senha
                    </label>
                    <input
                      id="account-password-confirm"
                      className="input-field"
                      type="password"
                      value={passwordConfirm}
                      autoComplete="new-password"
                      minLength={8}
                      onChange={(event) => setPasswordConfirm(event.target.value)}
                    />
                  </div>
                </div>
                <PasswordStrengthChecklist
                  password={password}
                  className="password-strength-checklist--account"
                />
              </div>
            )}

            {accountStatus && (
              <div className={`backup-status backup-status--flush ${accountStatus.type}`}>
                {accountStatus.message}
              </div>
            )}

            <div className="account-modal__actions">
              <button
                className="btn-ghost"
                type="button"
                onClick={() => setAccountOpen(false)}
              >
                Cancelar
              </button>
              <button className="btn-red" type="submit" disabled={savingAccount}>
                {savingAccount ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
